import { Component, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import {
  cdnBasePath,
  customCompanyLoginFlows,
  customCompanyHomeRedirects,
  specialModeLoginFlows,
} from '../../constants/constants';
import { devLog } from '../../utils/generic-utils';
import { CustomerTrainingService } from 'src/app/core/services/customer-training.service';
import { CustomerTrainingComponent } from '../customer-training/customer-training.component';
import { MatDialog } from '@angular/material/dialog';
import { filter, finalize, Subject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { UserSessionService } from '../../services/user-session.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  welcomeApps = [
    {
      icon: '../../../assets/images/icons/shop-icon-white.svg',
      iconSize: '45px',
      title: 'Shop',
      text: 'Easy to order with pre-configured product catalog',
    },
    {
      icon: '../../../assets/images/icons/ot-icon-white.svg',
      iconSize: '50px',
      title: 'Order Tracking',
      text: 'Check order status and shipping information',
    },
    {
      icon: '../../../assets/images/icons/rps-icon-white.svg',
      iconSize: '40px',
      title: 'LifecycleRepair',
      text: 'Create service ticket and timely repair status tracking of device repair',
    },
    {
      icon: '../../../assets/images/icons/atms-icon-white.svg',
      iconSize: '40px',
      title: 'ATMS',
      text: 'Track your inventory levels, statuses, and location of assets',
    },
  ];

  private readonly redirectToSignInMessage = 'Redirecting to sign in page&hellip;';

  private readonly redirecting = 'Redirecting';

  private readonly forgotPasswordMessage = 'Redirecting to forgot password page&hellip;';

  private readonly loadingDataMessage = 'Loading data&hellip;';

  // Store customer training files
  customerTrainingFiles: { name: string; url: string; lastModified: string }[] = [];

  // Store customer training files
  customerTrainingVideos: { name: string; url: string; lastModified: string }[] = [];

  // True if is fetching customer training
  isFetchingCustomerTraining: boolean = false;

  /** True if the training material icon can be displayed (there is data) */
  canDisplayCustomerTrainingIcon: boolean = false;

  // True if is fetching portals information
  isLoadingPortals: boolean = true;

  redirectMessage: string | null = null;

  // Stores all the portals information
  customerPortals: any[];

  // Stores the portal showed in the carousel
  carouselPortals: any[];

  // Stores the indeces to handle the carousel
  startIndex: number = 0;
  lastIndex: number = 3;

  // True when the component is handling the msal repsonse
  handleRedirect: boolean = false;

  /** True if the user is authenticated */
  private get isUserAuthenticated(): boolean {
    return this.authService.instance.getAllAccounts().length > 0;
  }

  customLoginPage: boolean = false;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private customerTrainingService: CustomerTrainingService,
    private matDialog: MatDialog,
    private http: HttpClient,
    private userSessionService: UserSessionService,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit() {
    if (!this.isUserAuthenticated) {
      // Get the params from the url if any
      const queryParams = this.route.snapshot.queryParams;
      let loginFlow: string;

      if (
        environment.environment !== 'prod' &&
        queryParams['specialMode'] !== undefined &&
        queryParams['specialMode'] !== null
      ) {
        const specialMode = queryParams['specialMode'];
        loginFlow = specialModeLoginFlows[specialMode];

        // Save in local storage the special mode name for logout
        localStorage.setItem('specialModeName', specialMode);
      } else if (queryParams['company'] !== undefined && queryParams['company'] !== null) {
        const customCompany = queryParams['company'];
        loginFlow = customCompanyLoginFlows[customCompany];

        // Save in local storage the company name for logout
        localStorage.setItem('sessionCompanyName', customCompany);
      }
      if (loginFlow) {
        this.customLoginPage = true;
        this.logInPressed(loginFlow);
      }
    }
    if (this.isUserAuthenticated) {
      await this.userSessionService.validateInactivityTime();

      // Redirect to page if needed
      const sessionCompanyName = localStorage.getItem('sessionCompanyName');
      if (sessionCompanyName && customCompanyHomeRedirects[sessionCompanyName]) {
        window.location.href = customCompanyHomeRedirects[sessionCompanyName];
      } else {
        window.location.href = '/secured/home';
      }
    }

    this.checkIfCustomerTraining();

    this.msalBroadcastService.msalSubject$.subscribe((subjectResponse: EventMessage) => {
      devLog({
        function: 'msalSubject$',
        data: Object.assign({}, subjectResponse),
        event: subjectResponse.eventType,
      });

      if (subjectResponse.eventType === EventType.LOGIN_START) {
        this.handleRedirect = true;
        this.redirectMessage = this.redirecting;
      }

      if (subjectResponse.eventType === EventType.LOGIN_FAILURE) {
        this.redirectMessage = null;
        this.handleRedirect = false;
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) =>
            status === InteractionStatus.None || status === InteractionStatus.HandleRedirect,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((res: InteractionStatus) => {
        if (res === InteractionStatus.HandleRedirect) {
          this.handleRedirect = true;
        } else {
          this.userSessionService.getActiveAccount();
        }
      });

    // Fetching portals information
    this.fetchCustomerPortals();

    // Carousel play
    setInterval(() => this.rightClick(), 5000);
  }

  ngOnDestroy(): void {
    this.handleRedirect = false;
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  openCustomerTraining(): void {
    const dialogRef = this.matDialog.open(CustomerTrainingComponent, {
      data: {
        videos: this.customerTrainingVideos,
        files: this.customerTrainingFiles,
      },
      height: 'auto',
      autoFocus: false,
      panelClass: 'customer-training-dialog',
    });
  }

  private async checkIfCustomerTraining(): Promise<void> {
    this.isFetchingCustomerTraining = true;
    this.canDisplayCustomerTrainingIcon = false;
    this.customerTrainingService
      .loginCustomerTrainingMaterial()
      .pipe(finalize(() => (this.isFetchingCustomerTraining = false)))
      .subscribe({
        next: (res: any) => {
          if ((res.hasFiles === true && res.data.files.length > 0) || res.data.videos.length > 0) {
            this.canDisplayCustomerTrainingIcon = true;
            if (res.data.files.length > 0) {
              res.data.files.forEach((file) => {
                file.url = `${cdnBasePath}/${file.name}`;
                this.customerTrainingFiles.push(file);
              });
            }
            if (res.data.videos.length > 0) {
              res.data.videos.forEach((file) => {
                file.url = `${cdnBasePath}/${file.name}`;
                this.customerTrainingVideos.push(file);
              });
            }
          }
        },
      });
  }

  logInPressed(loginFlow?: string) {
    if (loginFlow !== null && loginFlow !== undefined) {
      this.authService.instance.loginRedirect({
        scopes: (environment.azureB2C.guardConfig.authRequest as any).scopes,
        authority: `https://${environment.azureB2C.tenant}.b2clogin.com/${environment.azureB2C.tenant}.onmicrosoft.com/${loginFlow}`,
      });
    } else {
      this.authService.instance.loginRedirect();
      // Remove any company name and special mode name from local storage if any
      localStorage.removeItem('sessionCompanyName');
      localStorage.removeItem('specialModeName');
    }
  }

  private fetchCustomerPortals() {
    this.isLoadingPortals = true;

    this.http
      .get<any[]>(`${environment.restUrl}/v1/public/apps`)
      .pipe(finalize(() => (this.isLoadingPortals = false)))
      .subscribe({
        next: (portals) => {
          this.customerPortals = portals;
          this.carouselPortals = [
            this.customerPortals[0],
            this.customerPortals[1],
            this.customerPortals[2],
            this.customerPortals[3],
          ];
        },
        error: (error) => {
          this.isLoadingPortals = true;
        },
      });
  }

  leftClick() {
    if (this.startIndex === 0) {
      this.startIndex = this.customerPortals.length - 1;
      this.lastIndex--;
      this.carouselPortals.unshift(this.customerPortals[this.customerPortals.length - 1]);
      this.carouselPortals.pop();
    } else if (this.lastIndex === 0) {
      this.lastIndex = this.customerPortals.length - 1;
      this.startIndex--;
      this.carouselPortals.unshift(this.customerPortals[this.startIndex]);
      this.carouselPortals.pop();
    } else {
      this.startIndex--;
      this.lastIndex--;
      this.carouselPortals.unshift(this.customerPortals[this.startIndex]);
      this.carouselPortals.pop();
    }

    return;
  }

  rightClick() {
    if (this.lastIndex === this.customerPortals.length - 1) {
      this.lastIndex = 0;
      this.startIndex++;
      this.carouselPortals.shift();
      this.carouselPortals.push(this.customerPortals[0]);
    } else if (this.startIndex === this.customerPortals.length - 1) {
      this.startIndex = 0;
      this.lastIndex++;
      this.carouselPortals.shift();
      this.carouselPortals.push(this.customerPortals[this.lastIndex]);
    } else {
      this.startIndex++;
      this.lastIndex++;
      this.carouselPortals.shift();
      this.carouselPortals.push(this.customerPortals[this.lastIndex]);
    }

    return;
  }
}
